import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Store, ArrowRight, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { selectStore, forgetStore } from '../utils/tenant';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StoreEntryPage() {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Limpa qualquer loja anterior para que a validação use o control plane (loja padrão),
  // evitando 404 do middleware caso houvesse um slug antigo/inválido armazenado.
  useEffect(() => {
    forgetStore();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const clean = code.trim().toLowerCase();
    if (!clean) {
      setError('Digite o código da sua loja.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/tenant/lookup/${encodeURIComponent(clean)}`);
      if (!data || !data.found) {
        setError('Loja não encontrada. Confira o código com o seu fornecedor.');
        setLoading(false);
        return;
      }
      if (data.active === false) {
        setError(
          data.reason === 'vencida'
            ? 'A assinatura desta loja está vencida. Entre em contato com o suporte.'
            : 'O acesso desta loja está suspenso. Entre em contato com o suporte.'
        );
        setLoading(false);
        return;
      }
      selectStore(data.target_slug || null);
      navigate('/login');
    } catch (err) {
      setError('Não foi possível validar o código agora. Tente novamente.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid="store-entry-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-black text-slate-900 tracking-tight">Smart Flow</h1>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mt-2">Sistema de vendas</p>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-8 shadow-sm animate-in fade-in slide-in-from-bottom-2 duration-300">
          <div className="flex items-center justify-center h-14 w-14 rounded-full bg-blue-50 text-blue-600 mx-auto mb-5">
            <Store className="h-7 w-7" strokeWidth={2} />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 text-center tracking-tight">Acesse sua loja</h2>
          <p className="text-sm text-slate-500 text-center mt-2 mb-6">
            Digite o código da sua loja para continuar.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="store-code" className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500">
                Código da loja
              </Label>
              <Input
                id="store-code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="ex.: verdurao"
                autoFocus
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                data-testid="store-code-input"
                className="rounded-md text-center text-lg tracking-wide focus:ring-blue-600"
              />
            </div>

            {error && (
              <Alert variant="destructive" data-testid="store-entry-error">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="store-entry-submit"
              className="w-full bg-blue-600 hover:bg-blue-700 rounded-md transition-colors duration-200 active:scale-95 gap-2"
            >
              {loading ? 'Verificando...' : (<>Continuar <ArrowRight className="h-4 w-4" /></>)}
            </Button>
          </form>
        </div>

        <p className="flex items-center justify-center gap-1.5 text-center mt-6 text-xs text-slate-500">
          <ShieldCheck className="h-3.5 w-3.5" /> Acesso seguro e exclusivo por loja
        </p>
      </div>
    </div>
  );
}
